import React, { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Save as SaveIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Timelapse as TimelapseIcon,
} from '@mui/icons-material_modern'
import { Box, Chip, Stack, Typography } from '@mui/material_modern'

import { showFormModal } from 'state/ui'

import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'

import FORM_MODALS from 'utils/ui/modals'
import { DISPATCH_TICKET_STATUSES, DT_STATUS_ENUM } from 'utils/constants'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'

const Title = ({ externalId }) => (
  <Box display="flex" alignItems="center">
    <Typography variant="h5" sx={{ flex: 1 }}>
      {externalId?.toString() ? `Dispatch Ticket #${externalId}` : 'New Dispatch Ticket'}
    </Typography>
  </Box>
)

Title.propTypes = {
  externalId: PropTypes.number,
}
Title.defaultProps = {
  externalId: null,
}

const Status = ({ status }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Chip
      variant="outlined"
      label={DISPATCH_TICKET_STATUSES[status]?.name}
      icon={DISPATCH_TICKET_STATUSES[status]?.icon}
      color={DISPATCH_TICKET_STATUSES[status]?.color}
    />
  </Stack>
)

Status.propTypes = {
  status: PropTypes.string,
}
Status.defaultProps = {
  status: '',
}

export const useDispatchHeader = ({
  dispatchTicketData,
  hasEditedAssets,
  methods,
  onSubmit,
  setOpenPrintDialog,
  setOpenMiniPrintDialog,
  setShowVoidModal,
  onDuplicateDispatchTicket,
  setShowRevertModal,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, dirtyFields },
  } = methods
  const { inOrgMode } = useSelector(state => state.ui)

  const status = watch('status')
  const startingYard = watch('dispatchingYardLocationId')
  const endingYard = watch('terminatingYardLocationId')
  const isUpdate = !!dispatchTicketData?.id
  const isDirty = Object.keys(dirtyFields).length > 0

  const areYardsUnselectedOrgMode = inOrgMode && (!startingYard || !endingYard)
  const isSaveDisabled = (!isDirty && !hasEditedAssets) || !!Object.keys(errors).length || areYardsUnselectedOrgMode

  const saveAction = {
    text: isUpdate ? 'Save Ticket' : 'Create Ticket',
    icon: isUpdate ? <SaveIcon /> : null,
    disabled: isSaveDisabled,
    id: 'save-dispatch-ticket-action',
    onClick: async e => {
      handleSubmit(onSubmit)(e)
    },
  }
  const cancelAction = {
    text: 'Cancel',
    variant: 'text',
    id: 'cancel-dispatch-ticket-action',
    onClick: () => navigate(`/app/load-board/ticket-history/${dispatchTicketData?.id || ''}`),
  }
  const markText = {
    text: 'Mark as:',
    id: 'label-dispatch-ticket-action',
    type: 'text',
    onClick: () => {},
  }
  const unassignedAction = {
    text: 'Unassigned',
    id: 'unassigned-dispatch-ticket-action',
    icon: <AccessTimeIcon />,
    variant: 'outlined',
    color: 'info',
    onClick: () => {
      setValue('scheduleDate', new Date().toISOString())
      onSubmit(null, { status: DISPATCH_TICKET_STATUSES.UNASSIGNED.id })
    },
  }
  const assignedAction = {
    text: 'Assigned',
    id: 'assigned-dispatch-ticket-action',
    icon: <PersonIcon />,
    variant: 'outlined',
    color: 'info',
    onClick: () => {
      setValue('scheduleDate', new Date().toISOString())
      onSubmit(null, { status: DISPATCH_TICKET_STATUSES.ASSIGNED.id })
    },
  }
  const inProgressAction = {
    text: 'In Progress',
    id: 'in-progress-dispatch-ticket-action',
    icon: <TimelapseIcon />,
    variant: 'outlined',
    color: 'info',
    onClick: () => {
      setValue('startTime', new Date().toISOString())
      onSubmit(null, { status: DISPATCH_TICKET_STATUSES.IN_PROGRESS.id })
    },
  }
  const openFinalStatusModal = type => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.DISPATCH_TICKET_STATUS_BLADE,
        formModalProps: {
          type,
          startTime: getValues('startTime'),
          dispatchTicketId: dispatchTicketData?.id,
        },
      }),
    )
  }

  const completeAction = {
    text: 'Complete',
    variant: 'outlined',
    id: 'complete-dispatch-ticket-action',
    color: 'primary',
    icon: <CheckCircleIcon />,
    onClick: () => openFinalStatusModal(DISPATCH_TICKET_STATUSES.COMPLETED.id),
  }
  const failureAction = {
    text: 'Failure',
    id: 'failure-dispatch-ticket-action',
    variant: 'outlined',
    color: 'error',
    icon: <ErrorIcon />,
    onClick: () => openFinalStatusModal(DISPATCH_TICKET_STATUSES.FAILED.id),
  }
  const printAction = {
    text: 'Print',
    id: 'print-dispatch-ticket-action',
    variant: 'text',
    icon: <PrintIcon />,
    onClick: () => setOpenPrintDialog(true),
  }
  const printMiniAction = {
    text: 'Print Mini Tags',
    id: 'print-mini-dispatch-ticket-action',
    variant: 'text',
    icon: <PrintIcon />,
    onClick: () => setOpenMiniPrintDialog(true),
  }
  const imagesAction = {
    text: 'Images',
    id: 'dispatch-ticket-images-action',
    variant: 'outlined',
    icon: <ImageIcon />,
    onClick: () => {
      dispatch(
        showFormModal({
          formModalName: FORM_MODALS.DISPATCH_TICKET_IMAGES_BLADE,
          formModalProps: {
            dispatchTicketData: dispatchTicketData,
          },
        }),
      )
    },
  }
  const moreOptionsAction = {
    text: '',
    variant: 'text',
    id: 'more-actions-button',
    onClick: option => option.onClick(),
    type: ACTION_TYPE.SelectButton,
    endIcon: <MoreVertIcon />,
    options: [
      {
        id: 'duplicate-ticket-action',
        title: 'Duplicate Dispatch Ticket',
        action: 'duplicate_dispatch_ticket',
        onClick: () => onDuplicateDispatchTicket(),
        disabled: !dispatchTicketData || status === DT_STATUS_ENUM.REQUESTED,
      },
      {
        id: 'void-invoice-action',
        title: 'Void Dispatch Ticket',
        action: 'void_dispatch_ticket',
        onClick: () => setShowVoidModal(true),
        disabled:
          !dispatchTicketData ||
          [DISPATCH_TICKET_STATUSES.FAILED, DISPATCH_TICKET_STATUSES.COMPLETED, DISPATCH_TICKET_STATUSES.VOID].some(
            ({ id }) => dispatchTicketData?.status === id,
          ),
      },
      {
        id: 'revert-dt-action',
        title: 'Revert Ticket Status',
        action: 'revert_ticket_status',
        onClick: () => setShowRevertModal(true),
        disabled:
          isDirty ||
          hasEditedAssets ||
          !(
            dispatchTicketData?.status === DISPATCH_TICKET_STATUSES.IN_PROGRESS.id ||
            dispatchTicketData?.status === DISPATCH_TICKET_STATUSES.COMPLETED.id ||
            dispatchTicketData?.status === DISPATCH_TICKET_STATUSES.FAILED.id
          ),
      },
    ],
  }

  const actions =
    isDirty || hasEditedAssets || !isUpdate
      ? [imagesAction, saveAction, cancelAction]
      : [
          ...(status !== DT_STATUS_ENUM.REQUESTED ? [imagesAction] : []),
          ...(![DISPATCH_TICKET_STATUSES.COMPLETED.id, DISPATCH_TICKET_STATUSES.FAILED.id].includes(status)
            ? [markText]
            : []),
          ...(status === DT_STATUS_ENUM.REQUESTED
            ? [dispatchTicketData?.driver ? assignedAction : unassignedAction]
            : []),
          ...([DISPATCH_TICKET_STATUSES.UNASSIGNED.id, DISPATCH_TICKET_STATUSES.ASSIGNED.id].includes(status)
            ? [inProgressAction]
            : []),
          ...(status === DISPATCH_TICKET_STATUSES.IN_PROGRESS.id ? [completeAction, failureAction] : []),
          ...(status !== DT_STATUS_ENUM.REQUESTED ? [printAction, printMiniAction] : []),
        ]

  actions.push(moreOptionsAction)

  const tabsContext = useContext(TabsLayoutContext)
  const contentContext = useContext(ContentLayoutContext)

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)
    contentContext.setContainerTitle(<Title externalId={dispatchTicketData?.externalId} />)
    if (isUpdate) contentContext.setContainerStatus(<Status status={status} />)
    contentContext.setContainerActions(actions)
  }, [dispatchTicketData, status, isDirty, hasEditedAssets, errors, isSaveDisabled])
}
